import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import AssistantChart from './AssistantChart'
import { formatReportTimestamp } from '../../utils/reportGroups'

const COUNT_KEYS = [
  ['available', 'assistant.available', 'text-positive'],
  ['unavailable', 'assistant.unavailable', 'text-critical'],
  ['uptimePercent', 'assistant.uptimePercent', 'text-white'],
  ['equipmentAvailable', 'assistant.equipmentAvailable', 'text-positive'],
  ['equipmentUnavailable', 'assistant.equipmentUnavailable', 'text-critical'],
  ['noData', 'assistant.noData', 'text-muted-200'],
  ['pollingDisabled', 'assistant.pollingDisabled', 'text-muted-200'],
  ['notReady', 'assistant.notReady', 'text-critical'],
  ['ready', 'assistant.ready', 'text-positive'],
  ['nvrAvailable', 'assistant.nvrAvailable', 'text-positive'],
  ['nvrTotal', 'assistant.nvrTotal', 'text-white'],
  ['fireAvailable', 'assistant.fireAvailable', 'text-positive'],
  ['fireTotal', 'assistant.fireTotal', 'text-white'],
  ['fireNotConfigured', 'assistant.fireNotConfigured', 'text-muted-200'],
  ['doorAvailable', 'assistant.doorAvailable', 'text-positive'],
  ['doorTotal', 'assistant.doorTotal', 'text-white'],
  ['panicAvailable', 'assistant.stationOnLink', 'text-positive'],
  ['panicTotal', 'assistant.panicTotal', 'text-white'],
  ['schoolsEquipped', 'assistant.schoolsEquipped', 'text-white'],
  ['schoolsTotal', 'assistant.schoolsTotal', 'text-white'],
  ['schoolsWithoutChannel', 'assistant.schoolsWithoutChannel', 'text-muted-200'],
  ['coverageDoor', 'assistant.coverageDoor', 'text-white'],
  ['coveragePanic', 'assistant.coveragePanic', 'text-white'],
  ['coverageNvr', 'assistant.coverageNvr', 'text-white'],
  ['coverageFire', 'assistant.coverageFire', 'text-white'],
  ['withoutDoor', 'assistant.withoutDoor', 'text-muted-200'],
  ['withoutPanic', 'assistant.withoutPanic', 'text-muted-200'],
  ['withoutNvr', 'assistant.withoutNvr', 'text-muted-200'],
  ['withoutFire', 'assistant.withoutFire', 'text-muted-200'],
  ['cities', 'assistant.cities', 'text-white'],
  ['channels', 'assistant.channels', 'text-white'],
  ['channelsOnline', 'assistant.channelsOnline', 'text-positive'],
  ['channelsOffline', 'assistant.channelsOffline', 'text-critical'],
  ['channelsRecording', 'assistant.channelsRecording', 'text-positive'],
  ['channelsNotRecording', 'assistant.channelsNotRecording', 'text-critical'],
  ['disks', 'assistant.disks', 'text-white'],
  ['disksFaulty', 'assistant.disksFaulty', 'text-critical'],
  ['archiveKnown', 'assistant.archiveKnown', 'text-white'],
  ['archiveDepthMinDays', 'assistant.archiveDepthMinDays', 'text-white'],
  ['channelsWithoutSchoolChannel', 'assistant.channelsWithoutSchoolChannel', 'text-muted-200'],
  ['channelsPollingDisabled', 'assistant.channelsPollingDisabled', 'text-muted-200'],
  ['turnstilesAvailable', 'assistant.turnstileAvailable', 'text-positive'],
  ['turnstilesUnavailable', 'assistant.turnstileUnavailable', 'text-critical'],
  ['stationsOnLink', 'assistant.stationOnLink', 'text-positive'],
  ['stationsOffLink', 'assistant.stationOffLink', 'text-critical'],
  ['alerts', 'assistant.alertsCount', 'text-critical'],
  ['incidentsOpen', 'assistant.incidentsOpen', 'text-critical'],
  ['incidentsAccepted', 'assistant.incidentsAccepted', 'text-amber-200'],
  ['incidentsClosed', 'assistant.incidentsClosed', 'text-muted-200'],
  ['panicIncidents', 'assistant.panicIncidents', 'text-critical'],
  ['fireIncidents', 'assistant.fireIncidents', 'text-critical'],
  ['medianAcceptMinutes', 'assistant.medianAcceptMinutes', 'text-white'],
  ['maxAcceptMinutes', 'assistant.maxAcceptMinutes', 'text-white'],
  ['medianCloseMinutes', 'assistant.medianCloseMinutes', 'text-white'],
  ['maxCloseMinutes', 'assistant.maxCloseMinutes', 'text-white'],
  ['utilizationPercent', 'assistant.utilization', 'text-white'],
  ['trafficGb', 'assistant.trafficGb', 'text-white'],
  ['trafficInGb', 'assistant.trafficInGb', 'text-white'],
  ['trafficOutGb', 'assistant.trafficOutGb', 'text-white'],
  ['avgDownload', 'assistant.download', 'text-white'],
  ['avgUpload', 'assistant.upload', 'text-white'],
  ['samples', 'assistant.samples', 'text-muted-200'],
  ['regions', 'assistant.regions', 'text-white'],
  ['schools', 'assistant.schoolsCount', 'text-white'],
  ['total', 'assistant.total', 'text-white'],
]

const entityLink = 'text-sm text-muted-100 transition hover:text-white'
const quietButton =
  'inline-flex min-h-11 cursor-pointer items-center rounded-xl bg-white/[0.04] px-3 py-2 text-sm font-medium text-muted-200 transition hover:bg-white/[0.08] hover:text-white disabled:cursor-not-allowed disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400'

const Count = ({ label, value, tone }) => (
  <div className="rounded-xl bg-white/[0.04] px-4 py-3">
    <p className="text-[11px] font-medium uppercase tracking-[0.08em] text-muted-500">{label}</p>
    <p className={`mt-1 font-display text-2xl leading-none ${tone}`}>{value}</p>
  </div>
)

const EntityList = ({ title, items, render }) => {
  if (!items?.length) return null
  return (
    <div>
      <p className="mb-2 text-[11px] font-medium uppercase tracking-[0.08em] text-muted-500">{title}</p>
      <ul className="space-y-1.5">
        {items.map((item, index) => (
          <li key={item.id ?? item.name ?? index}>{render(item)}</li>
        ))}
      </ul>
    </div>
  )
}

const AssistantCard = ({ card, onSave, saving, saved, fallbackCapturedAt }) => {
  const { t, i18n } = useTranslation()
  const [saveError, setSaveError] = useState('')
  if (!card) return null

  const counts = card.counts || {}
  const capturedAt = formatReportTimestamp(card.capturedAt || fallbackCapturedAt, i18n.language)
  const visibleCounts = COUNT_KEYS.filter(([key]) => counts[key] != null)
  const links = Array.isArray(card.links) ? card.links : []
  const alerts = card.lists?.alerts
  const incidents = card.lists?.incidents
  const notReady = card.lists?.notReady
  const unavailableDevices = card.lists?.unavailable
  const unavailableSchools = card.lists?.unavailableSchools
  const schoolsTraffic = card.lists?.schools
  const statusLabel = (status) => t(`panic.status.${status}`, { defaultValue: status })
  const typeLabel = (deviceType) => {
    if (deviceType === 'nvr') return t('assistant.nvrs')
    if (deviceType === 'panic') return t('assistant.stations')
    if (deviceType === 'fire') return t('assistant.firePanels')
    return t('assistant.turnstiles')
  }

  const handleSave = async () => {
    if (!onSave || saved) return
    setSaveError('')
    try {
      await onSave(card)
    } catch (err) {
      setSaveError(err.message || t('assistant.saveError'))
    }
  }

  return (
    <section className="mt-3 space-y-4 rounded-2xl bg-surface-1 p-4">
      {capturedAt && (
        <p className="text-[11px] font-medium uppercase tracking-[0.08em] text-muted-500">
          {t('assistant.capturedAt', { time: capturedAt })}
        </p>
      )}
      {visibleCounts.length > 0 && (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          {visibleCounts.map(([key, labelKey, tone]) => (
            <Count key={key} label={t(labelKey)} value={counts[key]} tone={tone} />
          ))}
        </div>
      )}
      {card.chart && <AssistantChart chart={card.chart} />}
      <EntityList
        title={t('assistant.alertsList')}
        items={alerts}
        render={(item) => (
          <Link to={item.href || '/alerts'} className={entityLink}>
            {item.name}
            {item.severity ? ` · ${item.severity}` : ''}
          </Link>
        )}
      />
      <EntityList
        title={t('assistant.incidentsList')}
        items={incidents}
        render={(item) => {
          const kindLabel = item.kind === 'fire'
            ? t('assistant.fireIncident')
            : item.kind === 'panic'
              ? t('assistant.panicIncident')
              : null
          const device = item.device || item.station
          return (
            <Link to={item.href || '/panic-incidents'} className={entityLink}>
              #{item.id} {item.school}
              {kindLabel ? ` · ${kindLabel}` : ''}
              {device ? ` · ${device}` : ''}
              {item.operator ? ` · ${item.operator}` : ''}
              {` · ${statusLabel(item.status)}`}
              {item.hasResolution ? ` · ${t('assistant.hasResolution')}` : ''}
            </Link>
          )
        }}
      />
      <EntityList
        title={t('assistant.incidentGroups')}
        items={card.lists?.groups}
        render={(item) => (
          <span className="text-sm text-muted-100">
            {item.key} · {item.total}
          </span>
        )}
      />
      <EntityList
        title={t('assistant.notReadyList')}
        items={notReady}
        render={(item) => (
          <Link to={item.href || '/equipment'} className={entityLink}>
            {item.school} · {item.name} ({typeLabel(item.deviceType)})
            {item.reasons?.length ? ` · ${item.reasons.join(', ')}` : ''}
          </Link>
        )}
      />
      <EntityList
        title={t('assistant.unavailableList')}
        items={unavailableDevices}
        render={(item) => (
          <Link to={item.href || '/equipment'} className={entityLink}>
            {item.school} · {item.name} ({typeLabel(item.deviceType)})
          </Link>
        )}
      />
      <EntityList
        title={t('assistant.unavailableSchoolsList')}
        items={unavailableSchools}
        render={(item) => (
          <Link to={item.href || '/dashboard'} className={entityLink}>
            {item.name}
            {item.region ? ` · ${item.region}` : ''}
          </Link>
        )}
      />
      <EntityList
        title={t('assistant.regions')}
        items={card.lists?.regions}
        render={(item) => (
          <span className="text-sm text-muted-100">
            {item.name} · {item.schools}
          </span>
        )}
      />
      <EntityList
        title={t('assistant.cities')}
        items={card.lists?.cities}
        render={(item) => (
          <span className="text-sm text-muted-100">
            {item.name}
            {item.region ? ` · ${item.region}` : ''}
            {` · ${item.schools}`}
          </span>
        )}
      />
      {['withoutDoor', 'withoutPanic', 'withoutNvr', 'withoutFire'].map((key) => (
        <EntityList
          key={key}
          title={t(`assistant.${key}`)}
          items={card.lists?.[key]}
          render={(item) => (
            <Link to={item.href || '/equipment'} className={entityLink}>
              {item.name}
              {item.region ? ` · ${item.region}` : ''}
            </Link>
          )}
        />
      ))}
      <EntityList
        title={t('assistant.pollingDisabled')}
        items={card.lists?.pollingDisabled}
        render={(item) => (
          <Link to={item.href || '/equipment'} className={entityLink}>
            {item.school} · {item.name} ({typeLabel(item.deviceType)})
          </Link>
        )}
      />
      <EntityList
        title={t('assistant.notRecordingList')}
        items={card.lists?.notRecording}
        render={(item) => (
          <Link to={item.href || '/equipment'} className={entityLink}>
            {item.school} · {item.nvr} · {t('assistant.channelNumber', { id: item.channel })}
          </Link>
        )}
      />
      <EntityList
        title={t('assistant.channelOfflineList')}
        items={card.lists?.channelOffline}
        render={(item) => (
          <Link to={item.href || '/equipment'} className={entityLink}>
            {item.school} · {item.nvr} · {t('assistant.channelNumber', { id: item.channel })}
          </Link>
        )}
      />
      <EntityList
        title={t('assistant.disksFaultyList')}
        items={card.lists?.disksFaulty}
        render={(item) => (
          <Link to={item.href || '/equipment'} className={entityLink}>
            {item.school} · {item.nvr}
            {item.disk ? ` · ${t('assistant.diskNumber', { id: item.disk })}` : ''}
          </Link>
        )}
      />
      <EntityList
        title={t('assistant.archiveDepthList')}
        items={card.lists?.archiveDepth}
        render={(item) => (
          <Link to={item.href || '/equipment'} className={entityLink}>
            {item.school} · {item.nvr} · {t('assistant.channelNumber', { id: item.channel })} · {item.days}
          </Link>
        )}
      />
      <EntityList
        title={t('assistant.noData')}
        items={card.lists?.noData}
        render={(item) => (
          <Link to={item.href || '/equipment'} className={entityLink}>
            {item.school} · {item.name} ({typeLabel(item.deviceType)})
          </Link>
        )}
      />
      <EntityList
        title={t('assistant.schoolsTrafficList')}
        items={schoolsTraffic}
        render={(item) => (
          <span className="text-sm text-muted-100">
            {item.school}
            {item.trafficInGb != null ? ` · ↓ ${item.trafficInGb} GB` : ''}
            {item.trafficOutGb != null ? ` · ↑ ${item.trafficOutGb} GB` : ''}
            {item.utilizationPercent != null ? ` · ${item.utilizationPercent}%` : ''}
          </span>
        )}
      />
      <div className="flex flex-wrap items-center gap-2">
        {links.map((link) => (
          <Link
            key={link.href}
            to={link.href}
            className={quietButton}
          >
            {link.label}
          </Link>
        ))}
        {onSave && (
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || saved}
            className={quietButton}
          >
            {saved ? t('assistant.saved') : t('assistant.save')}
          </button>
        )}
      </div>
      {saveError && <p className="text-sm text-critical">{saveError}</p>}
    </section>
  )
}

export default AssistantCard
